import { useCallback, useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface ExportReadyDialogProps {
  /**
   * Function to download the export. Takes the export title and exportId.
   */
  onDownload: (exportId: string, format?: string) => Promise<void>;
  /**
   * Optional message to display in the dialog body.
   * @default "Your export is ready to download."
   */
  message?: string;
  /**
   * Optional title for the dialog.
   * @default "Export Ready"
   */
  dialogTitle?: string;
}

export function ExportReadyDialog({
  onDownload,
  message = 'Your export is ready to download.',
  dialogTitle = 'Export Ready',
}: ExportReadyDialogProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<string | undefined>();
  const [isDownloading, setIsDownloading] = useState(false);

  // Check for exportId in query params on mount
  useEffect(() => {
    const exportIdParam = searchParams.get('exportId');
    if (exportIdParam) {
      setExportId(exportIdParam);
      setExportFormat(searchParams.get('format') ?? undefined);
      setIsOpen(true);
      // Remove exportId and format from query params
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('exportId');
      newSearchParams.delete('format');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, []);

  const handleDownload = useCallback(async () => {
    if (!exportId) return;

    setIsDownloading(true);
    try {
      await onDownload(exportId, exportFormat);
      setIsOpen(false);
      setExportId(null);
      setExportFormat(undefined);
    } catch (error) {
      console.error('Failed to download export:', error);
      toast.error('Failed to download export. Please try again, or contact support@wexler.ai');
    } finally {
      setIsDownloading(false);
    }
  }, [exportId, exportFormat, onDownload]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setExportId(null);
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--download" />
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>
        <p>{message}</p>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleDownload} variant="primary" disabled={isDownloading}>
            {isDownloading ? 'Downloading...' : 'Download Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
